import { formatInTimeZone } from "date-fns-tz";

export const TIMEZONE_STORAGE_KEY = "preferred_time_zone";
export const TIMEZONE_DATA_STORAGE_KEY = "preferred_time_zone_data";
const DEFAULT_TIME_ZONE = "America/Bogota";

const FALLBACK_TIMEZONES = [
    "UTC",
    "America/Bogota",
    "America/Mexico_City",
    "America/Lima",
    "America/New_York",
    "America/Los_Angeles",
    "Europe/Madrid",
];

export const getSupportedTimeZones = () => {
    if (typeof Intl !== "undefined" && typeof Intl.supportedValuesOf === "function") {
        try {
            const zones = Intl.supportedValuesOf("timeZone");
            if (Array.isArray(zones) && zones.length > 0) {
                return zones;
            }
        } catch (error) {
            console.warn("No se pudieron obtener zonas horarias del navegador.", error);
        }
    }

    return FALLBACK_TIMEZONES;
};

export const getTimeZoneLabel = (timeZone, referenceDate = new Date()) => {
    try {
        const offset = formatInTimeZone(referenceDate, timeZone, "xxx");
        const cityLabel = timeZone.replaceAll("_", " ");
        return `(UTC${offset}) ${cityLabel}`;
    } catch {
        return timeZone;
    }
};

const parseOffsetToMinutes = (offsetText) => {
    const match = /^([+-])(\d{2}):(\d{2})$/.exec(offsetText || "");
    if (!match) return null;
    const sign = match[1] === "-" ? -1 : 1;
    const hours = parseInt(match[2], 10);
    const minutes = parseInt(match[3], 10);
    return sign * (hours * 60 + minutes);
};

export const getTimeZoneOffsetText = (timeZone, referenceDate = new Date()) => {
    try {
        return formatInTimeZone(referenceDate, timeZone, "xxx");
    } catch {
        return null;
    }
};

export const buildTimeZoneData = (timeZone, referenceDate = new Date()) => {
    const offsetText = getTimeZoneOffsetText(timeZone, referenceDate);
    return {
        timeZone,
        label: getTimeZoneLabel(timeZone, referenceDate),
        offsetText,
        offsetMinutes: parseOffsetToMinutes(offsetText),
    };
};

export const getTimeZoneOptions = () =>
    getSupportedTimeZones().map((timeZone) => {
        const tzData = buildTimeZoneData(timeZone);
        return {
            value: timeZone,
            label: tzData.label,
            offsetText: tzData.offsetText,
            offsetMinutes: tzData.offsetMinutes,
        };
    });

export const getBrowserTimeZone = () => {
    if (typeof Intl === "undefined" || typeof Intl.DateTimeFormat !== "function") {
        return DEFAULT_TIME_ZONE;
    }

    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIME_ZONE;
    } catch {
        return DEFAULT_TIME_ZONE;
    }
};

export const getCurrentTimeZone = () => {
    const saved = localStorage.getItem(TIMEZONE_STORAGE_KEY);
    if (saved) {
        return saved;
    }

    return getBrowserTimeZone();
};

export const getCurrentTimeZoneData = () => {
    const raw = localStorage.getItem(TIMEZONE_DATA_STORAGE_KEY);
    if (raw) {
        try {
            const parsed = JSON.parse(raw);
            if (parsed?.timeZone) return parsed;
        } catch {
            // ignore corrupt storage
        }
    }

    return buildTimeZoneData(getCurrentTimeZone());
};

export const saveCurrentTimeZone = (timeZoneOrOption) => {
    const selectedTimeZone = typeof timeZoneOrOption === "string"
        ? timeZoneOrOption
        : timeZoneOrOption?.value || getBrowserTimeZone();

    const data = buildTimeZoneData(selectedTimeZone);
    localStorage.setItem(TIMEZONE_STORAGE_KEY, selectedTimeZone);
    localStorage.setItem(TIMEZONE_DATA_STORAGE_KEY, JSON.stringify(data));
};
